import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { NotificationIconComponent } from '@kuiper/icons';
import { EMPLOYEE_ROUTES } from '../../../core/common/constants/employee.routing.constants';
import { MainService } from '../../../core/common/services/main/main.service';
import { NotificationCheckModeService } from '../../../core/common/services/notification-check-mode/notification-check-mode.service';
import { TenantService } from '../../../core/common/services/tenant/tenant.service';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import { NotificationComponent } from '../notification/notification.component';
import { Notification } from '../notification/notifications-type.enum';
import { SideMenuIconComponent } from '../side-menu/side-menu-icon/side-menu-icon.component';
import { NotificationFeedService } from './notification-feed.service';

@Component({
  selector: 'lib-notification-bell',
  imports: [
    NotificationIconComponent,
    NotificationComponent,
    ClickOutSideDirective,
    TranslocoPipe,
    SideMenuIconComponent,
  ],
  templateUrl: './notification-bell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly mainService = inject(MainService);
  private readonly notificationCheckModeService = inject(
    NotificationCheckModeService
  );
  protected readonly feed = inject(NotificationFeedService);
  private readonly destroy$ = new Subject<void>();

  readonly placement = input<'navbar' | 'side'>('navbar');

  protected readonly isOpen = computed(
    () => this.mainService.openOverlay() === 'notifications'
  );
  protected readonly wrapperClass = computed(() =>
    this.placement() === 'navbar'
      ? 'relative flex items-center'
      : 'flex items-center'
  );
  protected readonly buttonClass = computed(() => {
    if (this.placement() === 'navbar') {
      return 'relative cursor-pointer';
    }
    const state = this.isOpen()
      ? 'bg-neutral-100 text-neutral-900'
      : 'text-neutral-500 hover:bg-neutral-100';
    return `relative flex size-4xl items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ${state}`;
  });
  protected readonly iconClass = computed(() =>
    this.placement() === 'navbar'
      ? 'size-6 text-neutral-500'
      : 'size-5 do-not-transform'
  );
  protected readonly panelClass = computed(
    () =>
      'bg-white shadow-xl rounded-2xl overflow-hidden z-50 flex flex-col w-[29.25rem] ' +
      (this.placement() === 'navbar'
        ? 'notification-dropdown end-0 fixed top-6xl mx-lg'
        : 'absolute top-8 start-full ms-sm h-[41rem] max-h-[calc(100vh-8rem)]')
  );
  protected readonly panelHeight = computed(() =>
    this.placement() === 'navbar' ? this.feed.panelHeight() : null
  );

  ngOnInit(): void {
    this.feed.connect();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.feed.disconnect();
  }

  toggle(): void {
    this.mainService.openOverlay.set(this.isOpen() ? null : 'notifications');
  }

  close(): void {
    if (this.isOpen()) {
      this.mainService.openOverlay.set(null);
    }
  }

  goToNotifications(): void {
    this.router.navigate([
      this.tenantService.tenantPath(EMPLOYEE_ROUTES.NOTIFICATIONS),
    ]);
    this.close();
  }

  openSettings(): void {
    this.router.navigate(
      [
        this.tenantService.tenantPath(
          EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.PROFILE_DETAILS
        ),
      ],
      { queryParams: { tab: 'notifications' } }
    );
    this.close();
  }

  markRead(notification: Notification): void {
    this.feed.markRead(notification).subscribe();
  }

  openNotification(notification: Notification): void {
    this.close();
    const navigate = () =>
      this.notificationCheckModeService.navigateToNotification(
        notification,
        this.destroy$
      );
    this.feed
      .markRead(notification)
      .subscribe({ next: navigate, error: navigate });
  }
}
